import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, Phone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	useCrmWebSocket,
	type CrmSarvamReminderConfirmedEvent,
} from "@/hooks/useCrmWebSocket";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function leadsListPathForRole(role: string | undefined): string {
	if (role === "HOSPITAL_ADMIN") return "/hospital-admin/leads";
	if (role === "SUPER_ADMIN") return "/super-admin/leads";
	if (role === "SALES_PERSON" || role === "SALES_MANAGER") return "/sales-person/leads";
	return "/sales-person/leads";
}

function playUrgentChime() {
	try {
		const Ctx =
			window.AudioContext ||
			(window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
		if (!Ctx) return;
		const ctx = new Ctx();
		const now = ctx.currentTime;
		const gain = ctx.createGain();
		gain.gain.setValueAtTime(0.0001, now);
		gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
		gain.connect(ctx.destination);

		for (let i = 0; i < 3; i++) {
			const o = ctx.createOscillator();
			o.type = "triangle";
			o.frequency.setValueAtTime(740, now + i * 0.18);
			o.connect(gain);
			o.start(now + i * 0.18);
			o.stop(now + i * 0.18 + 0.14);
		}
		window.setTimeout(() => {
			void ctx.close().catch(() => undefined);
		}, 800);
	} catch {
		/* autoplay blocked */
	}
}

type QueueItem = CrmSarvamReminderConfirmedEvent & { key: string };

/**
 * Urgent modal when Sarvam Agent 2 confirms the patient is coming for OPD (sales + manager).
 */
export default function SarvamReminderConfirmedAlertDialog() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const [queue, setQueue] = useState<QueueItem[]>([]);
	const seenKeys = useRef(new Set<string>());

	const enabled =
		!!user?.id &&
		(user.role === "SALES_PERSON" || user.role === "SALES_MANAGER");

	const shouldShow = useCallback(
		(event: CrmSarvamReminderConfirmedEvent) => {
			if (!user?.id) return false;
			const recipients = event.recipientStaffIds;
			if (recipients?.length) return recipients.includes(user.id);
			const lead = event.lead;
			if (user.role === "SALES_PERSON") {
				return lead.assignedTo?.id === user.id;
			}
			if (user.role === "SALES_MANAGER") {
				if (!lead.assignedTo?.id) return true;
				if (lead.assignedTo.id === user.id) return true;
				return lead.assignedTo.managerId === user.id;
			}
			return false;
		},
		[user?.id, user?.role]
	);

	const onReminderConfirmed = useCallback(
		(event: CrmSarvamReminderConfirmedEvent) => {
			if (!event.lead?.id) return;
			if (!shouldShow(event)) return;
			const key =
				event.sarvamLeadCallId?.trim() ||
				`${event.lead.id}:${event.confirmedAt}`;
			if (seenKeys.current.has(key)) return;
			seenKeys.current.add(key);
			if (seenKeys.current.size > 100) {
				const first = seenKeys.current.values().next().value;
				if (first) seenKeys.current.delete(first);
			}

			void queryClient.invalidateQueries({ queryKey: ["leads"] });
			void queryClient.invalidateQueries({ queryKey: ["crm-billing-attribution"] });
			playUrgentChime();

			setQueue((prev) => [...prev, { ...event, key }]);
			setOpen(true);
		},
		[queryClient, shouldShow]
	);

	useCrmWebSocket({ enabled, onSarvamReminderConfirmed: onReminderConfirmed });

	const current = queue[0];
	const restCount = Math.max(0, queue.length - 1);

	const dismissCurrent = () => {
		setQueue((prev) => prev.slice(1));
	};

	useEffect(() => {
		if (queue.length === 0) setOpen(false);
		else setOpen(true);
	}, [queue.length]);

	if (!enabled || !current) return null;

	const lead = current.lead;

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) setQueue([]);
			}}
		>
			<DialogContent className="sm:max-w-md border-emerald-400/80 shadow-lg shadow-emerald-900/10">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-emerald-900">
						<CalendarCheck className="h-5 w-5 shrink-0" />
						Patient confirmed OPD visit
					</DialogTitle>
					<p className="text-sm text-muted-foreground pt-1">
						Agent 2 (appointment reminder) confirmed the customer is coming for the OPD
						consultation. Prepare for arrival — urgent attention.
					</p>
				</DialogHeader>
				<div className="rounded-md border border-emerald-200 bg-emerald-50/80 px-3 py-3 text-sm space-y-2">
					<div className="font-semibold text-emerald-950 text-base">{lead.patientName}</div>
					{lead.phone ? (
						<div className="flex items-center gap-1.5 text-emerald-900/90">
							<Phone className="h-3.5 w-3.5" />
							<span className="tabular-nums">{lead.phone}</span>
						</div>
					) : null}
					{current.appointmentLabel ? (
						<div className="text-emerald-800">
							<span className="text-emerald-700/80">Appointment: </span>
							<strong>{current.appointmentLabel} IST</strong>
						</div>
					) : null}
					{lead.assignedTo?.name ? (
						<div className="text-xs text-muted-foreground">Lead owner: {lead.assignedTo.name}</div>
					) : null}
					{restCount > 0 ? (
						<div className="text-xs text-emerald-700/80 pt-1">
							+{restCount} more confirmation{restCount === 1 ? "" : "s"} after this
						</div>
					) : null}
				</div>
				<DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
					<Button
						type="button"
						variant="outline"
						onClick={() => {
							dismissCurrent();
						}}
					>
						{restCount > 0 ? "Next" : "Dismiss"}
					</Button>
					<Button
						type="button"
						className="bg-emerald-700 hover:bg-emerald-800"
						onClick={() => {
							const base = leadsListPathForRole(user?.role);
							dismissCurrent();
							navigate(`${base}?openLeadId=${encodeURIComponent(lead.id)}`);
						}}
					>
						Open lead
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
